/**
 * Where the money is, and what the schedule will let it do next.
 *
 * These blocks were on the Overview because there was nowhere else for them.
 * That page answers three questions — is it making money, what is running, is
 * anything wrong — and the schedule's own figures answer a fourth: what the
 * strategy is *allowed* to do. A fourth question on a page built for three is
 * how it became a page of eleven panels.
 *
 * Nothing here is new. The allocation region, the per-pool rows and the
 * account curve are the same components, reading the same read models, moved
 * to the screen whose subject they are.
 */
import { LeadStrip } from "@/design-system/LeadStrip";
import { LimitBar } from "@/design-system/LimitBar";
import { PageHeader } from "@/design-system/PageHeader";
import { ErrorState, Forbidden, Loading, NotObserved } from "@/design-system/states/States";
import { metric, metricTestId } from "@/shared/metric/catalog";
import { money, percent, percentOf,
  sharePercent,
} from "@/shared/money/money";
import { useAccountAnalytics } from "@/shared/analytics/useAccountAnalytics";
import { useSearchParams } from "react-router";
import { readPeriod } from "@/shared/period/period";
import { Panel } from "@/design-system/Panel";
import type { CapitalResponse } from "@/shared/api/types.gen";

import { Allocation } from "./Allocation";
import { PerformancePreview } from "./PerformancePreview";
import { CashFlows } from "./CashFlows";
import { AccountLedger } from "./AccountLedger";
import { PnlCalendar } from "./PnlCalendar";
import { useOverview } from "@/shared/overview/useOverview";

/**
 * The three readings the schedule is made of, in the order they answer the
 * screen's question: what is allowed, what is taken, and what is left. Slot
 * counts are not among them: the pool table below already gives them per pool,
 * and a strip cell repeating their sum is the same fact twice on one screen.
 *
 * Each carries its second line because §4.1 requires one — a share of the
 * account, a bar against the ceiling, the size of the next slot, the count
 * actually running. Without them these are four boxes with a number in each,
 * which is what §16 refuses.
 */
function CapitalLead({
  capital,
}: {
  readonly capital: CapitalResponse;
}): React.JSX.Element {
  const used = percentOf(capital.committed, capital.allocatable);

  return (
    <LeadStrip
      testId="capital-lead"
      cells={[
        {
          label: metric("allocation.limit").label,
          lead: true,
          testId: "capital-lead-limit",
          value: (
            <span
              data-testid={metricTestId("allocation.limit")}
              data-metric-id="allocation.limit"
              data-metric-role="primary"
              title={capital.allocatable}
            >
              {money(capital.allocatable)}
            </span>
          ),
          note:
            capital.rate === "1" || capital.rate === "1.00"
              ? "Весь счёт доступен стратегии"
              : `${percent(capital.rate)} счёта доступно стратегии`,
        },
        {
          label: metric("account.committed_margin").label,
          testId: "capital-lead-committed",
          value: (
            <span
              data-testid={metricTestId("account.committed_margin")}
              data-metric-id="account.committed_margin"
              data-metric-role="context"
              title={capital.committed}
            >
              {money(capital.committed)}
            </span>
          ),
          note: <LimitBar filled={used} label={`${sharePercent(used)} лимита использовано`} />,
        },
        {
          label: metric("allocation.headroom").label,
          testId: "capital-lead-headroom",
          value: (
            <span
              data-testid={metricTestId("allocation.headroom")}
              data-metric-id="allocation.headroom"
              data-metric-role="context"
              title={capital.headroom}
            >
              {money(capital.headroom)}
            </span>
          ),
          // Captioned as calculation, per §10: neither this nor the slot size
          // is observed on the account, and set in the same face beside a
          // measured balance they read as one.
          note: `новый слот — от ${money(capital.target_slot_capital)} USDT`,
        },
      ]}
    />
  );
}

export function CapitalPage(): React.JSX.Element {
  const state = useOverview();
  const [search] = useSearchParams();
  // The same period the rest of the product reads, so a calendar opened from
  // Обзор shows the days that produced the figures there.
  const selected = useAccountAnalytics(readPeriod(search));

  if (state.status === "loading") return <Loading title="Считываю капитал…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "not-observed") return <NotObserved />;
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать капитал"
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }

  const { overview } = state;

  const capital = overview.performance.capital ?? null;

  return (
    <div className="flex flex-col gap-2" data-testid="capital-page">
      <PageHeader
        title="Капитал"
        description="Сколько капитала схема разрешает стратегии, сколько из него уже работает и где оно лежит."
        testId="capital-header"
      />

      {/* The figures this screen is about, and the reason equity is not among
          them: equity is a `global` metric and lives in the header, where it
          is true of the account rather than of this route. §8.5.1 of the
          baseline forbids repeating a global fact as a page headline, and a
          screen that opens with the same number as the bar above it has said
          nothing yet. The subject of Капитал is the ceiling the schedule
          draws, so that is what leads. */}
      {capital !== null && <CapitalLead capital={capital} />}

      <Panel title="Распределение и лимиты" labelledBy="capital-heading">
        <Allocation capital={capital} overview={overview} />
      </Panel>

      {/* What a slot is, beside how many of them are working. The allocation
          above cannot be checked without it. */}

      {/* The curve says which way over the period; the calendar says which
          days. A bad Tuesday and a slow month look identical on a line. */}
      {/* Not stretched. A month grid is as tall as its month; levelled against
          the column beside it, it carried four hundred pixels of empty panel
          under the last week. Two columns of different lengths are honest when
          their contents are different lengths. */}
      <div className="grid min-w-0 grid-cols-1 items-start gap-2 xl:grid-cols-12">
        <div className="flex min-w-0 flex-col gap-2 xl:col-span-6">
          <PnlCalendar daily={selected.data?.daily ?? []} loading={selected.isPending} />
          {/* Under the calendar rather than beside the figures above: those
              are governed by the period control and this is not, and the space
              the month grid leaves is where it fits without pushing anything
              below the fold. */}
          <AccountLedger ledger={selected.data?.ledger ?? null} />
        </div>
        <div className="flex min-w-0 flex-col gap-2 xl:col-span-6">
          <PerformancePreview />
          {/* Directly under the curve it qualifies: a rise that followed a
              deposit is not the same rise, and the two belong in one glance. */}
          <CashFlows
            flows={selected.data?.flows ?? []}
            loading={selected.isPending}
          />
        </div>
      </div>

    </div>
  );
}
